import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

export const shopifyApi = {
  // Fetch products directly from Shopify (without storing locally)
  async listProducts(businessId: string, limit: number = 10) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products?limit=${encodeURIComponent(limit)}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch products');
    }
    return res.json();
  },

  // Get products from local database (requires businessId or organizationId)
  async getLocalProducts(
    businessId: string | undefined,
    limit: number = 20,
    offset: number = 0,
    filters?: { search?: string; status?: string; vendor?: string; type?: string }
  ) {
    if (!businessId) {
      throw new Error('businessId or organizationId is required to fetch Shopify products');
    }
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    if (filters?.search) params.set('search', filters.search);
    if (filters?.status && filters.status !== 'all') params.set('status', filters.status);
    if (filters?.vendor && filters.vendor !== 'all') params.set('vendor', filters.vendor);
    if (filters?.type && filters.type !== 'all') params.set('type', filters.type);
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/local?${params.toString()}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch local products');
    }
    return res.json();
  },

  // Fetch store meta: vendors, product types, collections
  async getStoreMeta(businessId: string): Promise<{ vendors: string[]; productTypes: string[]; collections: { id: number; title: string }[] }> {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/meta`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) return { vendors: [], productTypes: [], collections: [] };
    return res.json();
  },

  // Create product in Shopify
  async createProduct(businessId: string, productData: any) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(productData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to create product');
    }
    return res.json();
  },

  // Update product in Shopify
  async updateProduct(businessId: string, productId: string, productData: any) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/${productId}`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(productData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to update product');
    }
    return res.json();
  },

  // Delete product in Shopify
  async deleteProduct(businessId: string, productId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/${productId}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to delete product');
    }
    return res.json();
  },

  // Sync products from Shopify to local database
  async syncProducts(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/sync`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}), // Send empty object to avoid Fastify empty body error
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to sync products');
    }
    return res.json();
  },

  // Get orders from local database (requires businessId or organizationId)
  async getLocalOrders(businessId: string | undefined, limit: number = 20, offset: number = 0, filters?: {
    search?: string;
    financialStatus?: string;
    fulfillmentStatus?: string;
    tag?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    if (!businessId) {
      throw new Error('businessId or organizationId is required to fetch Shopify orders');
    }
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.financialStatus) params.set('financial_status', filters.financialStatus);
    if (filters?.fulfillmentStatus) params.set('fulfillment_status', filters.fulfillmentStatus);
    if (filters?.tag) params.set('tag', filters.tag);
    if (filters?.sortBy) params.set('sort_by', filters.sortBy);
    if (filters?.sortOrder) params.set('sort_order', filters.sortOrder);
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/orders/local?${params}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch local orders');
    }
    return res.json();
  },

  // Sync orders from Shopify to local database
  async syncOrders(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/orders/sync`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}), // Send empty object to avoid Fastify empty body error
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to sync orders');
    }
    return res.json();
  },

  // Sync customers from Shopify to local database
  async syncCustomers(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/customers/sync`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}), // Send empty object to avoid Fastify empty body error
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to sync customers');
    }
    return res.json();
  },

  // Check connection status (businessId is optional - if not provided, checks at organization level)
  async checkConnection(businessId?: string) {
    const url = businessId
      ? `${API_CONFIG.BASE_URL}/api/v1/shopify/${businessId}/connection`
      : `${API_CONFIG.BASE_URL}/api/v1/shopify/connection`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to check connection');
    }
    const data = await res.json();
    return data;
  },

  // --- Webhook Management ---

  async listWebhooks(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/webhooks`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to list webhooks');
    }
    const data = await res.json();
    return data.webhooks as Array<{
      id: number;
      address: string;
      topic: string;
      created_at: string;
      updated_at: string;
      format: string;
      api_version: string;
    }>;
  },

  async createWebhook(businessId: string, topic: string, address: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/webhooks`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ topic, address }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to create webhook');
    }
    return res.json();
  },

  async updateWebhook(businessId: string, webhookId: string | number, address: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/webhooks/${webhookId}`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ address }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to update webhook');
    }
    return res.json();
  },

  async deleteWebhook(businessId: string, webhookId: string | number) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/webhooks/${webhookId}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to delete webhook');
    }
    return res.json();
  },

  // Disconnect the current Shopify connection
  async disconnect(businessId?: string | null) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/shopify/disconnect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        businessId: businessId ?? undefined,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to disconnect Shopify');
    }
    return res.json();
  },

  // Sync Shopify products to WhatsApp catalog
  async syncToCatalog(
    businessAccountId: string,
    businessId: string,
    options?: { limit?: number; skipExisting?: boolean }
  ) {
    const url = `${API_CONFIG.BASE_URL}/commerce/sync/shopify`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        businessAccountId,
        businessId,
        limit: options?.limit || 20,
        skipExisting: options?.skipExisting !== false,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = ApiError.fromResult(
        { ...json, statusCode: res.status },
        'Failed to sync products to WhatsApp catalog',
      );
      // Some callers read errorData for per-product sync results — keep the contract.
      (error as any).errorData = json;
      throw error;
    }
    return json;
  },

  // Get abandoned checkouts from local database
  async getLocalCheckouts(businessId?: string, limit: number = 20, offset: number = 0) {
    const url = businessId
      ? `${API_CONFIG.BASE_URL}/api/v1/shopify/${businessId}/checkouts?limit=${limit}&offset=${offset}`
      : `${API_CONFIG.BASE_URL}/api/v1/shopify/checkouts/local?limit=${limit}&offset=${offset}`;

    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      if (res.status === 404) return { checkouts: [] };
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch checkouts');
    }
    const data = await res.json();
    return data;
  },

  // Update tags on a local order record
  async updateOrderTags(businessId: string, orderId: string, tags: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/orders/${orderId}/tags`;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tags }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to update order tags');
    }
    return res.json();
  },

  // --- Order Tags ---

  async listOrderTags(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch order tags');
    }
    return res.json();
  },

  async createOrderTag(businessId: string, tagName: string, isDefault = false) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tagName, isDefault }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to create order tag');
    }
    return res.json();
  },

  async deleteOrderTag(businessId: string, tagId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags/${tagId}`;
    const res = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to delete order tag');
    }
    return res.json();
  },

  async setDefaultOrderTag(businessId: string, tagId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags/${tagId}/default`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to set default order tag');
    }
    return res.json();
  },

  async unsetDefaultOrderTag(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags/default`;
    const res = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to clear default order tag');
    }
    return res.json();
  },

  // Sync abandoned checkouts from Shopify to local database
  async syncCheckouts(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/checkouts/sync`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}), // Send empty object to avoid Fastify empty body error
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to sync checkouts');
    }
    return res.json();
  },
};

// Currency formatting helper
export function formatCurrency(amount: number, currencyCode: string = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    // Fallback if currency code is invalid
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

// Returns the symbol for a currency code (e.g. 'USD' -> '$', 'INR' -> '₹',
// 'EUR' -> '€'). Derived from the connected Shopify store's currency — we do
// NOT let users pick an arbitrary currency because Shopify variant prices are
// always denominated in the store's single currency. Falls back to the raw
// code (e.g. 'AED') when the runtime can't resolve a symbol.
export function getCurrencySymbol(currencyCode: string = 'USD'): string {
  const code = (currencyCode || 'USD').toUpperCase();
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const symbol = parts.find(p => p.type === 'currency')?.value;
    return symbol || code;
  } catch {
    return code;
  }
}
